import React, { useState } from 'react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Search, Copy, Check, Heart, Leaf, AlertTriangle, ChevronDown, ChevronRight, Home, ArrowLeft } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ofnrData from './data/ofnr_trainer_data.json';

/**
 * Standalone Feelings & Needs Tool Page
 * Accessible at /feelings-needs
 */
export default function FeelingsNeedsPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('satisfied');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [copiedWord, setCopiedWord] = useState(null);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const copyToClipboard = (word) => {
    navigator.clipboard.writeText(word);
    setCopiedWord(word);
    toast.success(`Copied "${word}" to clipboard`);
    setTimeout(() => setCopiedWord(null), 2000);
  };

  const filterWords = (words) => {
    if (!searchTerm) return words;
    return words.filter(word => 
      word.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filterCategory = (category, words) => {
    const filtered = filterWords(words);
    if (!searchTerm) return { show: true, words };
    if (filtered.length > 0) return { show: true, words: filtered };
    if (category.toLowerCase().includes(searchTerm.toLowerCase())) return { show: true, words };
    return { show: false, words: [] };
  };

  const tabs = [
    { id: 'satisfied', label: 'Feelings (Needs Met)', icon: Heart, color: 'text-green-400', bgColor: 'bg-green-500/20', borderColor: 'border-green-500/50' },
    { id: 'unsatisfied', label: 'Feelings (Needs Unmet)', icon: Heart, color: 'text-rose-400', bgColor: 'bg-rose-500/20', borderColor: 'border-rose-500/50' },
    { id: 'needs', label: 'Universal Needs', icon: Leaf, color: 'text-emerald-400', bgColor: 'bg-emerald-500/20', borderColor: 'border-emerald-500/50' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate(-1)}
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-700" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
                  <Heart className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Feelings & Needs Explorer
                  </h1>
                  <p className="text-xs text-slate-400">NVC Vocabulary Reference Tool</p>
                </div>
              </div>
            </div>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <Home className="h-4 w-4 mr-2" />
              Home
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Introduction */}
        <div className="text-center mb-6">
          <p className="text-slate-300 max-w-2xl mx-auto">
            Explore the vocabulary of Nonviolent Communication. Click any word to copy it to your clipboard.
            Use these words to express yourself authentically and connect with your needs.
          </p>
        </div>

        {/* Steve's Wisdom - Body Awareness */}
        <div className="max-w-3xl mx-auto mb-6 bg-gradient-to-r from-amber-900/30 to-orange-900/20 border border-amber-500/40 rounded-xl p-5" data-testid="steve-wisdom">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xl">💡</span>
            </div>
            <div>
              <h3 className="text-amber-300 font-bold text-lg mb-2">Steve's Recommendation</h3>
              <p className="text-amber-100/90 leading-relaxed italic">
                "When you are in a situation and you aren't exactly sure of the words, <span className="text-amber-300 font-semibold">read through the list</span> — your body will react to the words that matter."
              </p>
            </div>
          </div>
        </div>

        {/* Pearl: Stimulus not Triggered */}
        <div className="max-w-3xl mx-auto mb-8 bg-gradient-to-r from-purple-900/30 to-indigo-900/20 border border-purple-500/40 rounded-xl p-5" data-testid="stimulus-pearl">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xl">🌟</span>
            </div>
            <div>
              <h3 className="text-purple-300 font-bold text-lg mb-2">A Powerful Reframe</h3>
              <p className="text-purple-100/90 leading-relaxed">
                We are not "<span className="text-rose-300 line-through">triggered</span>" by others — which implies they control our feelings. 
                <span className="text-purple-300 font-semibold"> We receive stimuli.</span> Our feelings arise from our own needs being met or unmet.
              </p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative max-w-md mx-auto mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search feelings or needs..."
            className="pl-12 py-6 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-400 text-lg rounded-xl"
            data-testid="feelings-search-input"
          />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all ${
                  activeTab === tab.id 
                    ? `${tab.bgColor} ${tab.color} ${tab.borderColor} border-2 shadow-lg` 
                    : 'bg-slate-800/50 text-slate-400 hover:text-white border-2 border-transparent hover:border-slate-600'
                }`}
              >
                <Icon className="h-5 w-5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tip */}
        <div className="flex items-center justify-center gap-2 text-slate-400 text-sm mb-6">
          <Copy className="h-4 w-4" />
          <span>Click any word to copy it to your clipboard</span>
        </div>

        {/* Feelings When Satisfied */}
        {activeTab === 'satisfied' && (
          <div className="space-y-4" data-testid="satisfied-content">
            {Object.entries(ofnrData.feelingsWhenSatisfied).map(([category, words]) => {
              const { show, words: filteredWords } = filterCategory(category, words);
              if (!show) return null;
              
              const isExpanded = expandedCategories[category] !== false;
              
              return (
                <div key={category} className="bg-slate-800/30 rounded-xl border border-green-700/30 overflow-hidden">
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                  >
                    <span className="font-semibold text-green-300 uppercase tracking-wide">{category}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-green-400/60 text-sm">{filteredWords.length} words</span>
                      {isExpanded ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-6 pb-5 flex flex-wrap gap-2">
                      {filteredWords.map((word) => (
                        <button
                          key={word}
                          onClick={() => copyToClipboard(word)}
                          className={`px-4 py-2 rounded-full text-sm transition-all ${
                            copiedWord === word 
                              ? 'bg-green-500 text-white scale-105' 
                              : 'bg-green-900/30 text-green-200 hover:bg-green-800/50 hover:scale-105 border border-green-700/30'
                          }`}
                        >
                          {copiedWord === word && <Check className="h-3 w-3 inline mr-1" />}
                          {word}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Feelings When NOT Satisfied */}
        {activeTab === 'unsatisfied' && (
          <div className="space-y-4" data-testid="unsatisfied-content">
            {Object.entries(ofnrData.feelingsWhenNotSatisfied).map(([category, words]) => {
              const { show, words: filteredWords } = filterCategory(category, words);
              if (!show) return null;
              
              const isExpanded = expandedCategories[category] !== false;
              
              return (
                <div key={category} className="bg-slate-800/30 rounded-xl border border-rose-700/30 overflow-hidden">
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                  >
                    <span className="font-semibold text-rose-300 uppercase tracking-wide">{category}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-rose-400/60 text-sm">{filteredWords.length} words</span>
                      {isExpanded ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-6 pb-5 flex flex-wrap gap-2">
                      {filteredWords.map((word) => (
                        <button
                          key={word}
                          onClick={() => copyToClipboard(word)}
                          className={`px-4 py-2 rounded-full text-sm transition-all ${
                            copiedWord === word 
                              ? 'bg-rose-500 text-white scale-105' 
                              : 'bg-rose-900/30 text-rose-200 hover:bg-rose-800/50 hover:scale-105 border border-rose-700/30'
                          }`}
                        >
                          {copiedWord === word && <Check className="h-3 w-3 inline mr-1" />}
                          {word}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Non-Feelings Warning */}
            <div className="mt-8 bg-amber-900/20 border border-amber-700/30 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <span className="font-semibold text-amber-300">Watch Out: Non-Feeling Words</span>
                  <p className="text-amber-200/70 text-sm">These words blame others and give away your power</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {ofnrData.nonFeelingWords.map((word) => (
                  <span key={word} className="px-3 py-1.5 bg-amber-900/40 text-amber-300 rounded-full text-sm border border-amber-700/30">
                    {word}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Needs */}
        {activeTab === 'needs' && (
          <div className="space-y-4" data-testid="needs-content">
            {Object.entries(ofnrData.needsByCategory).map(([category, needs]) => {
              const { show, words: filteredNeeds } = filterCategory(category, needs);
              if (!show) return null;
              
              const isExpanded = expandedCategories[category] !== false;
              
              return (
                <div key={category} className="bg-slate-800/30 rounded-xl border border-emerald-700/30 overflow-hidden">
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                  >
                    <span className="font-semibold text-emerald-300 uppercase tracking-wide">{category}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-400/60 text-sm">{filteredNeeds.length} needs</span>
                      {isExpanded ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-6 pb-5 flex flex-wrap gap-2">
                      {filteredNeeds.map((need) => (
                        <button
                          key={need}
                          onClick={() => copyToClipboard(need)}
                          className={`px-4 py-2 rounded-full text-sm transition-all ${
                            copiedWord === need 
                              ? 'bg-emerald-500 text-white scale-105' 
                              : 'bg-emerald-900/30 text-emerald-200 hover:bg-emerald-800/50 hover:scale-105 border border-emerald-700/30'
                          }`}
                        >
                          {copiedWord === need && <Check className="h-3 w-3 inline mr-1" />}
                          {need}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Marshall's Wisdom - Please/Thank You */}
        <div className="mt-12 max-w-3xl mx-auto bg-gradient-to-r from-teal-900/30 to-emerald-900/20 border border-teal-500/40 rounded-xl p-6" data-testid="marshall-wisdom">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-2xl">🦒</span>
            </div>
            <div>
              <h3 className="text-teal-300 font-bold text-lg mb-2">Marshall's Wisdom</h3>
              <p className="text-teal-100/90 leading-relaxed mb-3">
                "The harshest possible statements directed towards you — if you strip away everything — they are saying <span className="text-teal-300 font-bold">'please'</span> (or 'thank you'). <span className="text-emerald-300 font-semibold">Find out what they are saying 'please' for.</span>"
              </p>
              <p className="text-slate-300 text-sm">
                It's always about finding <span className="text-teal-300 font-semibold">power WITH</span> people instead of power FROM them or AGAINST them.
              </p>
            </div>
          </div>
        </div>

        {/* Connection is Natural */}
        <div className="mt-6 max-w-3xl mx-auto bg-gradient-to-r from-rose-900/30 to-pink-900/20 border border-rose-500/40 rounded-xl p-5" data-testid="connection-natural">
          <div className="text-center">
            <p className="text-rose-100/90 leading-relaxed text-lg">
              <span className="text-rose-300 font-bold">Connection is actually the natural way.</span> To be in a judgmental, criticizing, imbalanced engagement does not have to be how we are. <span className="text-rose-300 font-semibold">We can choose.</span>
            </p>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-12 text-center">
          <p className="text-slate-500 text-sm">
            Based on Nonviolent Communication (NVC) by Marshall Rosenberg
          </p>
          <p className="text-slate-600 text-xs mt-2">
            Part of the Belonging Sessions ecosystem
          </p>
        </div>
      </main>
    </div>
  );
}
